import React, { useEffect, useRef } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useI18n } from '../i18n/i18nContext';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { pushToDataLayer, clearEcommerce } from '../utils/dataLayer';
import { formatPrice } from '@shared/utils';

export const GiftCardPurchaseSuccessPage: React.FC = () => {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useLocalizedNavigate();

  useDocumentTitle(t.giftCard.successPageTitle || 'Gift Card Purchase Success');

  const { state } = location;
  const STORAGE_KEY = 'giftCardSuccessData';

  const hasTrackedPurchase = useRef(false);

  // Store state in sessionStorage when it's available
  useEffect(() => {
    if (state && state.code) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  }, [state]);

  // Try to get state from navigation or sessionStorage
  let giftCardData = state;
  if (!giftCardData || !giftCardData.code) {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        giftCardData = JSON.parse(stored);
      } catch (e) {
        console.error('Failed to parse stored gift card data:', e);
      }
    }
  }

  useEffect(() => {
    if (giftCardData && giftCardData.code && !hasTrackedPurchase.current) {
      hasTrackedPurchase.current = true;
      clearEcommerce();
      
      pushToDataLayer('purchase', {
        content: { category: 'gift_card' },
        ecommerce: {
          transaction_id: giftCardData.paymentIntentId || giftCardData.code,
          value: giftCardData.amount,
          currency: 'EUR',
          items: [{
            item_id: giftCardData.giftCardItemId || 'GIFT_CARD',
            item_name: 'Gift Card',
            item_category: 'Gift Card',
            value: giftCardData.amount
          }]
        },
        customer: {
          email: giftCardData.senderEmail || '',
          phone: giftCardData.senderPhone || ''
        }
      });
    }
  }, [giftCardData]);

  // If still no data, redirect to home
  if (!giftCardData || !giftCardData.code) {
    return <Navigate to="/" replace />;
  }

  const {
    code,
    amount,
    senderName,
    recipientName,
    recipientEmail,
    message,
  } = giftCardData;

  // Clear stored data when navigating away
  const handleGoHome = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    navigate('/');
  };

  return (
    <div className="container mx-auto px-4 py-12 max-w-3xl">
      <div className="bg-white p-8 rounded-lg shadow-lg text-center">
        <div className="flex justify-center mb-6">
          <svg className="w-16 h-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold mb-4">{t.giftCard.successPageTitle || 'Gift Card Purchase Successful!'}</h1>
        <p className="text-lg text-gray-700 mb-8">{t.giftCard.successPageMessage || 'Your gift card has been purchased and sent to the recipient.'}</p>

        <div className="bg-gray-50 p-6 rounded-lg text-left shadow-inner">
          <h2 className="text-2xl font-semibold mb-6 text-center">{t.giftCard.detailsTitle || 'Purchase Details'}</h2>
          <div className="mb-6">
            <strong className="block text-gray-800 mb-2">{t.giftCard.giftCardCode || 'Gift Card Code'}</strong>
            <div className="text-2xl font-mono text-indigo-600 bg-indigo-50 p-3 rounded-md text-center my-3">
              {code}
            </div>
            <p className="text-sm text-gray-600 text-center mt-2">{t.giftCard.codeInfo || 'The recipient will receive this code via email.'}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <strong className="block text-gray-800 mb-1">{t.giftCard.amount || 'Amount'}</strong>
              <p className="text-gray-700">{formatPrice(amount)}</p>
            </div>
            <div>
              <strong className="block text-gray-800 mb-1">{t.giftCard.recipientName}</strong>
              <p className="text-gray-700">{recipientName}</p>
            </div>
            <div>
              <strong className="block text-gray-800 mb-1">{t.giftCard.yourName || 'Sender Name'}</strong>
              <p className="text-gray-700">{senderName}</p>
            </div>
            <div>
              <strong className="block text-gray-800 mb-1">{t.giftCard.deliveryEmail || 'Recipient Email'}</strong>
              <p className="text-gray-700">{recipientEmail}</p>
            </div>
          </div>
          {message && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <strong className="block text-gray-800 mb-2">{t.giftCard.personalMessage || 'Personal Message'}</strong>
              <p className="text-gray-700 italic bg-blue-50 p-4 rounded-md">{message}</p>
            </div>
          )}
        </div>
        <p className="mt-8 text-gray-600">{t.giftCard.emailConfirmation?.replace('{{email}}', recipientEmail) || `A confirmation email has been sent to ${recipientEmail}.`}</p>
        <button
          onClick={handleGoHome}
          className="mt-8 bg-indigo-600 text-white py-2 px-6 rounded-md hover:bg-indigo-700"
        >
          {t.common.goHome || 'Back to Home'}
        </button>
      </div>
    </div>
  );
};

export default GiftCardPurchaseSuccessPage;
